import type { AiOperation } from './models.js';

/**
 * Prompt caching: what the provider will discount, and what it takes to qualify.
 *
 * ## The mechanism, and the one rule that follows from it
 *
 * A `/v1/responses` call is billed at the full input rate unless its *prefix* — the request as
 * rendered, from the first byte forward — matches one the provider has served recently. Match is
 * exact and it is a prefix: it ends at the first byte that differs, and nothing after that point is
 * discounted however identical it happens to be.
 *
 * So the whole of prompt caching, as a piece of engineering, is one rule: **arrange every prompt
 * from most stable to least.** Method before evidence, evidence before the question, and the user's
 * own words last. Anything stable that sits behind something variable is paying full price for no
 * reason, and no amount of tuning elsewhere recovers it.
 *
 * The saving is large where it applies. Sol is $4.00/M uncached against $0.40/M cached — a 90%
 * discount on the part of a call that is, for this app, most of the call: the wand ships a ~9,700
 * token system prompt to answer a fifteen-word request.
 *
 * ## Why there is a minimum, and why it is the awkward part
 *
 * Nothing below {@link PROMPT_CACHE_MINIMUM_TOKENS} is cached at all. That is not a rounding
 * threshold — it is a cliff, and it makes "is this prompt cacheable?" a question with a real answer
 * rather than a matter of degree. Two of this app's prompts sit just under it — the texture recipe
 * and the tag-suggest pre-pass — and the temptation there is to pad them until they qualify. That
 * would be paying for tokens on every call to get a discount on tokens nobody needed, and it would
 * degrade the prompt into the bargain. Neither prompt is padded, and
 * `tests/reference/PromptCaching.test.ts` says so where somebody would look.
 *
 * Like the rest of `shared/`, this compiles under both tsconfigs.
 */

/**
 * The shortest prefix the provider will cache, in tokens. Below this, nothing is cached.
 *
 * Checked by hand alongside the price table, on the `PRICES_CHECKED` date in `pricing.ts`; when that
 * date moves, this number is one of the facts that was re-checked.
 */
export const PROMPT_CACHE_MINIMUM_TOKENS = 1024;

/**
 * Characters per token, roughly, for English prose with a lot of punctuation in it.
 *
 * Deliberately crude and only ever used to answer "is this prompt anywhere near the minimum?". It is
 * not a tokenizer, the app does not ship one, and every number derived from it is written as an
 * approximation wherever it appears. Where a prompt lands within ±15% of the minimum, the honest
 * answer is that we do not know whether it caches — which is itself a useful thing to be able to
 * say, and is why {@link clearsCacheMinimum} has a margin instead of a comparison.
 */
export const APPROXIMATE_CHARS_PER_TOKEN = 4;

/** A rough token count for a prompt section. Not a tokenizer; see {@link APPROXIMATE_CHARS_PER_TOKEN}. */
export function approximateTokens(text: string): number {
  return Math.round(text.length / APPROXIMATE_CHARS_PER_TOKEN);
}

/**
 * Whether a prefix is long enough that caching is confidently expected to engage.
 *
 * The 15% margin is the estimator's error bar, not a safety factor. A prompt inside it gets `false`,
 * which reads as "do not count on this" rather than "this does not cache" — the distinction that
 * stops a test asserting a fact about the provider that we cannot actually observe from here.
 */
export function clearsCacheMinimum(text: string): boolean {
  return approximateTokens(text) > PROMPT_CACHE_MINIMUM_TOKENS * 1.15;
}

/**
 * The cache key sent with a call, so requests that share a prefix are routed together.
 *
 * The provider caches per machine, and without a key it spreads a busy account's traffic across
 * several — which is invisible until you have enough users to notice that a prompt you carefully
 * made byte-identical is still being billed at the full rate. The key is what pins one prompt family
 * to one place.
 *
 * **It is the operation and nothing else.** Not the user, not the session, not the site. A per-user
 * key would fragment the cache exactly where the hosted deployment stands to gain most — every
 * account shares one API key there, so every account's wand casts share one prefix — and it would
 * put an account identifier in a request body for no benefit at all.
 */
export function promptCacheKey(operation: AiOperation): string {
  return `lushglade-${operation}`;
}
